import styles from './GitLab.module.scss'
import Contact from '../pages/Contact'
import BestPractice from '../pages/BestPractice'
import Features from '../pages/Features'
import Introduction from '../pages/Introduction'
import Top from '../pages/Top'
import Flow from '../pages/Flow'
import PriceList from '../pages/PriceList'
import QA from '../pages/QA'
import CallToAction from '../pages/CallToAction'
import Architecture from '../pages/Architecture'
import { useRef } from 'react'
import useScrollHideContact from '../hooks/useScrollHideContact'
import EndToEnd from './EndToEnd'
import MailmagazineLink from 'components/mailmagazine/MailmagazineLink/MailmagazineLink'
import NewsLp from '../pages/NewsLp'

const ContactFormId = '06700a48-ebe7-4bd2-bd4a-1e3592e703c3'

const paths = [
  { name: 'TOP', url: '/' },
  { name: 'Services', url: '/services' },
  { name: 'GitLab', url: '#' },
]

const TopContents = {
  smallTitle: '開発ライフサイクル全体を一元管理',
  title: ['GitLabで実現する', '効率的なDevOpsワークフロー'],
  text: '計画、開発、テスト、デプロイまで、すべてを1つのプラットフォームで統合します。',
  imgLink: '/images/LP/LP202407/Logo/LogoGitLab.png',
}

const IntroductionContents = {
  title: '複雑な開発プロセスがプロジェクトの進行を妨げていませんか？',
  list: [
    {
      text: ['複数のツールを使用することによる管理の煩雑さと連携の困難さ'],
      icon: '/images/LP/LP202407/Icon/Introduction/BsPalette2.svg',
    },
    {
      text: ['チーム間のコミュニケーションとコラボレーションの不足'],
      icon: '/images/LP/LP202407/Icon/Introduction/BsPersonX.svg',
    },
    {
      text: ['セキュリティとコンプライアンスの確保が困難'],
      icon: '/images/LP/LP202407/Icon/Introduction/BsShieldExclamation.svg',
    },
  ],
}
const BestPracticeContents = {
  heading: ['DevOpsプラクティスを統合し、', '開発効率を最大化します'],
  text: [
    'GitLab導入支援サービスは、開発ライフサイクル全体を一元管理し、チームの生産性を大幅に向上させます。',
    'バージョン管理、CI/CD、セキュリティ、プロジェクト管理など、DevOpsに必要なすべての機能を1つのプラットフォームに統合。',
    '当社のGitLab認定エキスパートが、お客さまのニーズに合わせた最適なGitLab環境の設計から運用までをトータルでサポートします。',
  ],
}

const ArchitectureContents = {
  title: 'GitLab',
  imgLink: ['/images/LP/LP202407/Architectures/architecture-gitlab01.png'],
}

const FeaturesContents = {
  title: 'GitLab',
  items: [
    {
      num: '01',
      itemTitle: '統合されたDevOpsプラットフォーム',
      description:
        'GitLabを活用した包括的なDevOps環境を構築。計画、開発、テスト、デプロイ、モニタリングまでの全プロセスを1つのプラットフォームで管理し、開発ライフサイクルを効率化します。強力なCI/CDパイプラインを構築し、コードのビルド、テスト、デプロイを自動化します。',
      src: '/images/LP/LP202407/img/gitlab01.jpeg',
    },
    {
      num: '02',
      itemTitle: 'セキュリティとコンプライアンスの強化',
      description:
        'GitLabの組み込みセキュリティ機能を活用し、開発プロセス全体でセキュリティを確保。当社のセキュリティ専門家が、最新のベストプラクティスに基づいた安全な開発環境を構築します。',
      src: '/images/LP/LP202407/img/img02.jpeg',
    },
    {
      num: '03',
      itemTitle: 'カスタマイズと柔軟な統合',
      description:
        '豊富な経験を活かし、GitLabと既存のツールやワークフローとの統合をサポート。お客さまの開発プロセスに合わせたカスタマイズにより、最大限の効果を引き出します。',
      src: '/images/LP/LP202407/img/img01.jpeg',
    },
  ],
}

const FlowContents = [
  {
    num: 1,
    title: '現状分析',
    description: 'お客さまの開発プロセスと課題を詳細に分析します。',
    src: '/images/LP/LP202407/Icon/BsBarChartLine.svg',
  },
  {
    num: 2,
    title: '最適化プラン策定',
    description: 'GitLabを活用した最適な開発環境を設計します。',
    src: '/images/LP/LP202407/Icon/BsDiagram3.svg',
  },
  {
    num: 3,
    title: '環境構築と継続的な最適化',
    description:
      'GitLab環境を構築し、既存のツールや processes の移行をサポートします。また、継続的な改善をサポートします。',
    src: '/images/LP/LP202407/Icon/BsPersonCheck.svg',
  },
]

const FAQContents = [
  {
    num: 1,
    question: 'GitLabは、どのような規模の企業や開発チームに適していますか？',
    answer:
      'GitLabは、スタートアップから大企業まで、あらゆる規模の企業に適しています。特に、開発プロセスの効率化とチームコラボレーションの向上を目指す組織に最適です。',
  },
  {
    num: 2,
    question: '既存の開発ツールからGitLabへの移行は難しいですか？',
    answer:
      '当社の専門チームが、スムーズな移行をサポートします。GitLabは多くの一般的なツールとの統合をサポートしており、段階的な移行が可能です。',
  },
  {
    num: 3,
    question: 'GitLabの導入により、具体的にどのような効果が期待できますか？',
    answer:
      'GitLab導入により、開発サイクルの短縮、チームコラボレーションの向上、セキュリティの強化、運用コストの削減などが期待できます。具体的な効果は、お客さまの環境や目標に応じて異なりますが、多くの場合、生産性が20-30%向上したという報告があります。',
  },
  {
    num: 4,
    question: 'オンプレミス環境でもGitLabを利用できますか？',
    answer:
      'はい、GitLabはクラウド版とセルフホスト版の両方を提供しています。お客さまのニーズや既存のインフラ環境を考慮し、最適な導入方法を提案します。',
  },
  {
    num: 5,
    question: 'AitriaのGitLab導入支援サービスの特徴は何ですか？',
    answer:
      '当社の特徴は、GitLabの認定資格を持つ専門家による手厚いサポート、豊富な導入実績に基づく知見、お客さまのビジネスニーズに合わせたカスタマイズ能力です。また、導入後のトレーニングや継続的な最適化支援により、GitLabの利点を最大限に活用できるよう支援します。',
  },
]

const inPocketList = [
  {
    image: '/images/inPocket/Gitlab/git_github_gitlab.png',
    title: 'GitとGitHub、GitLabの違い：バージョン管理と開発プラットフォームの選び方',
    date: '2024.8.27',
    href: 'https://aitria.co.jp/in-pocket/11556',
  },
  {
    image: '/images/inPocket/Gitlab/knowledge_gitlab-.png',
    title: 'GitLabとは？できることや料金プランをわかりやすく解説',
    date: '2024.8.16',
    href: 'https://aitria.co.jp/in-pocket/11414',
  },
  {
    image: '/images/inPocket/Gitlab/story_gitlab.png',
    title: 'AIで生成する物語-第2話：セキュリティを強化するためにGitLabの導入を検討してみた話',
    date: '2024.8.15',
    href: 'https://aitria.co.jp/in-pocket/11390',
  },
]

const topics = [
  {
    title:
      '特徴量エンジニアリングを使って生成AIによるUIデザインと画面レイアウトのアウトプットをコントロールする技術を開発',
    date: '2024年9月6日',
    src: '/pressrelease/3726',
  },
  {
    title: 'アート引越センターが提供する、AIが自動見積りをするアプリの開発支援をいたしました',
    date: '2024年5月21日',
    src: '/pressrelease/3369',
  },
  {
    title: '開発生産性の向上のためGitHubが提供する「GitHub Copilot」を導入',
    date: '2023年5月24日',
    src: '/pressrelease/2769',
  },
]

const CallToActionContents = {
  heading: ['GitLabで開発プロセスを革新し、', '生産性を向上させませんか？'],
}

const GitLab: React.FC = () => {
  const contactRef = useRef<HTMLDivElement>(null)
  const contentsRef = useRef<HTMLDivElement>(null)

  useScrollHideContact(contactRef, contentsRef)
  return (
    <>
      <article className={styles.LPPage}>
        <div className={styles.contents} ref={contentsRef}>
          <Top contents={TopContents} paths={paths} />
          <Introduction contents={IntroductionContents} />
          <BestPractice contents={BestPracticeContents} />
          <Architecture contents={ArchitectureContents} />
          <EndToEnd />
          <Features contents={FeaturesContents} />
          <Flow contents={FlowContents} />
          <PriceList />
          <QA contents={FAQContents} />
          <NewsLp inPocketList={inPocketList} topics={topics} />
          <CallToAction contents={CallToActionContents} />
        </div>
        <div ref={contactRef} className={styles.contact_wrapper}>
          <Contact formId={ContactFormId} />
        </div>
        <div>
          <MailmagazineLink />
        </div>
      </article>
    </>
  )
}
export default GitLab
